import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useForm, FormContext } from 'react-hook-form'
import { renderFormByType } from './subforms/renderFormByType'
import { PopTypeSelector } from './subforms/PopTypeSelector'
import { PopFormValues, PopType } from '../types'
import { Box, Flex, useToast } from '@chakra-ui/core'
import { PopSwitch } from './fileds/PopSwitch'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { AppDispatch } from 'config/redux'
import { createPopAdminV, editPop, isAiPopType, setIsLoading } from 'redux/actions/pops'
import { getDefaultProfileImageUrl } from './utils'
import { isEmpty } from 'lodash'
import { prepareLinkPopData } from '../model/linkPop'

export interface PopFormShellRef {
  submit: () => void
}

interface PopFormShellProps {
  onClose: () => void
  onCreated?: (data: PopFormValues) => void
  validateOnly?: boolean
  initialPopData?: PopFormValues | null
}

export const PopFormShell = forwardRef<PopFormShellRef, PopFormShellProps>(
  ({ onClose, onCreated, validateOnly, initialPopData }, ref) => {
    const isEdit = !isEmpty(initialPopData)
    const toast = useToast()
    const methodsRef = useRef<any>(null)
    const dispatch: AppDispatch = useDispatch()
    const { rid } = useSelector((state: RootState) => state.config.config)

    const [selectedType, setSelectedType] = useState<PopType>(initialPopData?.pop_type || PopType.Default)
    const previousLongRangeRef = useRef(initialPopData?.is_long_range_pop || false)

    const methods = useForm<PopFormValues>({
      defaultValues: initialPopData || {},
      mode: 'onChange',
    })

    methodsRef.current = methods
    const handleTypeChange = (type: PopType) => {
      if (type === selectedType) return

      const profileImageUrl = methods.getValues('data.profile_image_url')
      if (!profileImageUrl || profileImageUrl === getDefaultProfileImageUrl(selectedType)) {
        methods.setValue('data.profile_image_url', getDefaultProfileImageUrl(type))
      }

      if (type === PopType.LinkPop) {
        previousLongRangeRef.current = !!methods.getValues('is_long_range_pop')
      } else if (selectedType === PopType.LinkPop) {
        methods.setValue('is_long_range_pop', previousLongRangeRef.current)
      }

      setSelectedType(type)
    }

    const prepareFormData = (data: PopFormValues) => {
      const preparedData = prepareLinkPopData(data)
      const body: any = validateOnly ? { ...preparedData, is_scheduled: true } : preparedData

      if (body.pop_type !== PopType.LinkPop) {
        if (!body.data.video) {
          body.data.video = null
        }

        if (!body.data.pop_image_url) {
          body.data.pop_image_url = null
        }
      }

      if (!body.data.profile_image_url) {
        body.data.profile_image_url = getDefaultProfileImageUrl(body.pop_type)
      }

      delete body.is_send_push_notification
      delete body.data.is_repop_send_push_notification

      return body
    }

    const handleEditPop = async (body: any) => {
      delete body.is_scheduled
      delete body.rid

      const editBody = {
        pop_id: initialPopData?.pop_id,
        ...body,
      }

      const res = await dispatch(editPop({ body: editBody, isAiPop: isAiPopType(body.pop_type) }))

      if (res.success) {
        toast({
          title: 'Pop updated',
          description: 'Pop updated successfully',
          status: 'success',
          position: 'top-right',
        })

        body.data.prompt_id = res.prompt_id
        const dataToReturn = { ...body, pop_id: res.data, prompt_id: res.prompt_id }

        onCreated?.({ ...dataToReturn })
        onClose()
      }
    }

    const handleCreatePop = async (body: any) => {
      const res = await dispatch(createPopAdminV({ body }))

      if (res.success) {
        toast({
          title: 'Pop created',
          description: 'Pop created successfully',
          status: 'success',
          position: 'top-right',
        })

        onCreated?.({ ...body, pop_id: res.data })
        onClose()
      }
    }

    const onSubmit = async (data: PopFormValues) => {
      dispatch(setIsLoading(true))

      try {
        const preparedData = prepareFormData(data)

        if (isEdit) {
          await handleEditPop(preparedData)
        } else {
          await handleCreatePop(preparedData)
        }
      } catch (e) {
        console.log(e)
        toast({
          title: 'Pop creation failed',
          description: 'Pop creation failed',
          status: 'error',
          position: 'top-right',
        })
      } finally {
        dispatch(setIsLoading(false))
      }
    }
    const isLongRangePop = methods.getValues('is_long_range_pop') as boolean

    useImperativeHandle(ref, () => ({
      submit: () => {
        methods.handleSubmit(onSubmit)()
      },
      onClose,
    }))

    useEffect(() => {
      if (rid) {
        methods.setValue('rid', rid)
      }
    }, [rid, methods])

    useEffect(() => {
      methods.setValue('pop_type', selectedType)
      if (selectedType === PopType.LinkPop) methods.setValue('is_long_range_pop', true)
      methods.clearError()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedType])

    return (
      <FormContext<PopFormValues> {...methods}>
        <Flex w="100%" alignItems="center" justifyContent="space-between" style={{ columnGap: '10px' }}>
          <Box pointerEvents={isEdit ? 'none' : 'auto'} opacity={isEdit ? 0.5 : 1}>
            <PopTypeSelector value={selectedType} onChange={handleTypeChange} />
          </Box>

          <Flex flexDir="column" alignItems="flex-end" style={{ rowGap: '10px' }}>
            <PopSwitch
              name="is_long_range_pop"
              title="enable_long_range"
              defaultValue={isLongRangePop}
              isDisabled={selectedType === PopType.LinkPop}
            />

            {selectedType === PopType.RePop && <PopSwitch name="data.is_pop_post" title="create_pop_as_post" />}

            {(selectedType === PopType.AiPop || selectedType === PopType.AiRePop) && (
              <Flex flexDir="column" alignItems="flex-end" style={{ rowGap: '10px' }}>
                <PopSwitch name="data.is_inj_reports" title="enable_injury_reports" />
                <PopSwitch name="data.is_voice_mode" title="enable_voice_mode" />
              </Flex>
            )}
          </Flex>
        </Flex>
        {renderFormByType(selectedType, isEdit)}
      </FormContext>
    )
  }
)
